import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import {
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Input,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';

import {
    createWorkflowStepConfig,
    deleteWorkflowStepConfig,
    getWorkflowDefinitions,
    getWorkflowStepConfigs,
    updateWorkflowStepConfig,
} from '../../api';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { Toast, enqueueSnackbar, ToastSuccess, ToastError } from '../../../shared/components/Notifier';
import { NO_PERMISSIONS_ERROR_MSG } from '../../../shared/lib/Localization';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: { padding: theme.spacing(2) },
        controls: { display: 'flex', gap: 16, marginBottom: theme.spacing(2), alignItems: 'center' },
        formControl: { minWidth: 260 },
        actionsSelect: { minWidth: 260 },
        chips: { display: 'flex', flexWrap: 'wrap', gap: 4 },
    }),
);

interface WorkflowStepDefinition {
    code: string;
    label: string;
    permission_codename: string | null;
    optional: boolean;
    default_actions: string[];
}

interface WorkflowDefinition {
    workflow_type: string;
    label: string;
    steps: WorkflowStepDefinition[];
}

interface WorkflowActionDefinition {
    key: string;
    label: string;
}

interface StepConfigRow {
    id?: number;
    enabled: boolean;
    actionKeys: string[];
    isOverridden: boolean;
}

const WorkflowStepOverridesScreen = (props: any) => {
    const classes = useStyles({});
    const [definitions, setDefinitions] = useState<WorkflowDefinition[]>([]);
    const [actions, setActions] = useState<WorkflowActionDefinition[]>([]);
    const [workflowType, setWorkflowType] = useState<string>('');
    const [stepRows, setStepRows] = useState<Record<string, StepConfigRow>>({});
    const [isLoading, setLoading] = useState(false);
    const [savingStep, setSavingStep] = useState<string | null>(null);

    const businessId = props.userData?.selectedBusiness?.business_id;

    const showToast = (message: string, options = ToastError) => {
        props.showToast({ message, options });
    };

    useEffect(() => {
        getWorkflowDefinitions()
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                setDefinitions(data.workflows || []);
                setActions(data.actions || []);
                if (!workflowType && data.workflows && data.workflows.length > 0) {
                    setWorkflowType(data.workflows[0].workflow_type);
                }
            })
            .catch(() => showToast('Failed to load workflow definitions.'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedDefinition = definitions.find((d) => d.workflow_type === workflowType) || null;

    const loadStepConfigs = () => {
        if (!businessId || !workflowType || !selectedDefinition) {
            return;
        }
        setLoading(true);
        getWorkflowStepConfigs(businessId, workflowType)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                const configs = (data && data.results) || data || [];
                const configByStep: Record<string, any> = {};
                configs.forEach((c: any) => { configByStep[c.step_code] = c; });
                const rows: Record<string, StepConfigRow> = {};
                selectedDefinition.steps.forEach((step) => {
                    const override = configByStep[step.code];
                    rows[step.code] = override
                        ? {
                            id: override.id,
                            enabled: override.enabled,
                            actionKeys: override.action_keys || [],
                            isOverridden: true,
                        }
                        : {
                            enabled: true,
                            actionKeys: step.default_actions || [],
                            isOverridden: false,
                        };
                });
                setStepRows(rows);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                showToast('Failed to load step overrides.');
            });
    };

    useEffect(() => {
        loadStepConfigs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, workflowType, definitions]);

    const updateRow = (stepCode: string, patch: Partial<StepConfigRow>) => {
        setStepRows((prev) => ({ ...prev, [stepCode]: { ...prev[stepCode], ...patch } }));
    };

    const handleSaveStep = (step: WorkflowStepDefinition) => {
        if (!businessId) {
            return;
        }
        const row = stepRows[step.code];
        const payload = {
            business: businessId,
            workflow_type: workflowType,
            step_code: step.code,
            enabled: row.enabled,
            action_keys: row.actionKeys,
        };
        setSavingStep(step.code);
        const request = row.id
            ? updateWorkflowStepConfig(row.id, payload)
            : createWorkflowStepConfig(payload);
        request
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                setSavingStep(null);
                showToast('Step override saved.', ToastSuccess);
                loadStepConfigs();
            })
            .catch((error: any) => {
                setSavingStep(null);
                let msg = error && error.body && error.body.error
                    ? error.body.error
                    : 'Failed to save step override.';
                if (error && (error.status === 403 || error.status === 401)) {
                    msg = NO_PERMISSIONS_ERROR_MSG;
                }
                showToast(msg);
            });
    };

    const handleResetStep = (step: WorkflowStepDefinition) => {
        const row = stepRows[step.code];
        if (!row || !row.id) {
            return;
        }
        setSavingStep(step.code);
        deleteWorkflowStepConfig(row.id)
            .catch(handleError)
            .then(checkStatus)
            .then(() => {
                setSavingStep(null);
                showToast('Reset to code default.', ToastSuccess);
                loadStepConfigs();
            })
            .catch(() => {
                setSavingStep(null);
                showToast('Failed to reset step override.');
            });
    };

    if (!businessId) {
        return (
            <Paper className={classes.root}>
                <Typography color="textSecondary">
                    Select a business from the top bar to configure its workflow steps.
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper className={classes.root}>
            <Typography variant="h6" gutterBottom>Stock Order Workflows</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                Per-business overrides of code-defined workflow steps. Optional steps can be
                disabled; the actions fired on each step can be re-bound. Steps with no override
                use the code defaults.
            </Typography>

            <div className={classes.controls}>
                <FormControl className={classes.formControl}>
                    <InputLabel>Workflow Type</InputLabel>
                    <Select
                        value={workflowType}
                        onChange={(e) => setWorkflowType(e.target.value as string)}
                    >
                        {definitions.map((d) => (
                            <MenuItem key={d.workflow_type} value={d.workflow_type}>
                                {d.label} ({d.workflow_type})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {isLoading && <CircularProgress size={22} />}
            </div>

            {selectedDefinition && (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Step</TableCell>
                            <TableCell>Permission</TableCell>
                            <TableCell>Optional</TableCell>
                            <TableCell>Enabled</TableCell>
                            <TableCell>Actions Fired</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {selectedDefinition.steps.map((step) => {
                            const row = stepRows[step.code] || {
                                enabled: true,
                                actionKeys: step.default_actions || [],
                                isOverridden: false,
                            };
                            return (
                                <TableRow key={step.code}>
                                    <TableCell>
                                        <strong>{step.label}</strong><br />
                                        <Typography variant="caption" color="textSecondary">{step.code}</Typography>
                                    </TableCell>
                                    <TableCell>{step.permission_codename || '—'}</TableCell>
                                    <TableCell>{step.optional ? 'Yes' : 'No'}</TableCell>
                                    <TableCell>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={row.enabled}
                                                    disabled={!step.optional}
                                                    onChange={(e) => updateRow(step.code, { enabled: e.target.checked })}
                                                />
                                            }
                                            label=""
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <FormControl className={classes.actionsSelect}>
                                            <Select
                                                multiple
                                                value={row.actionKeys}
                                                onChange={(e) => updateRow(step.code, { actionKeys: e.target.value as string[] })}
                                                input={<Input />}
                                                renderValue={(selected) => (
                                                    <div className={classes.chips}>
                                                        {(selected as string[]).map((value) => (
                                                            <Chip key={value} label={value} size="small" />
                                                        ))}
                                                    </div>
                                                )}
                                            >
                                                {actions.map((action) => (
                                                    <MenuItem key={action.key} value={action.key}>
                                                        <Checkbox checked={row.actionKeys.indexOf(action.key) > -1} />
                                                        <ListItemText primary={action.label} secondary={action.key} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        {row.isOverridden
                                            ? <Chip label="Override" size="small" color="primary" />
                                            : <Chip label="Code default" size="small" />}
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            color="primary"
                                            disabled={savingStep === step.code}
                                            onClick={() => handleSaveStep(step)}
                                        >
                                            {savingStep === step.code ? <CircularProgress size={16} /> : 'Save'}
                                        </Button>
                                        {row.isOverridden && (
                                            <Button
                                                size="small"
                                                disabled={savingStep === step.code}
                                                onClick={() => handleResetStep(step)}
                                            >
                                                Reset
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </Paper>
    );
};

const mapStateToProps = (state: any) => ({
    userData: state.userLocalData,
});

const mapDispatchToProps = (dispatch: any) => ({
    showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkflowStepOverridesScreen);
